import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { GoAndTrackLink } from "../../lib/goAndTrack";

// Short labels keep the desktop nav on a single line.
const NAV = [
  { to: "/disposable-package-tracker", label: "Category Guide" },
  { to: "/disposable-smart-labels", label: "Smart Labels" },
  { to: "/gps-vs-ble-vs-cellular", label: "Technology" },
  { to: "/disposable-vs-reusable-trackers", label: "Comparison" },
  { to: "/disposable-tracking-cost", label: "Cost" },
  { to: "/glossary", label: "Glossary" },
  { to: "/faqs", label: "FAQs" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-xl border-b border-line"
    >
      <div className="container-x h-20 flex items-center justify-between gap-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0"
          data-testid="brand-home-link"
        >
          <span className="h-8 w-8 grid place-items-center bg-ink text-white font-mono text-[11px] tracking-widest">
            DSL
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-heading font-semibold text-ink text-[15px] whitespace-nowrap">
              Disposable Smart Labels
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-subtle mt-1 whitespace-nowrap">
              Powered by <GoAndTrackLink className="normal-case tracking-normal" />
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6 mx-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-[-0.005em] whitespace-nowrap transition-colors ${
                  isActive ? "text-brand" : "text-ink-muted hover:text-ink"
                }`
              }
              data-testid={`nav-${n.to.replace(/\//g, "")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <Link
            to="/request-a-trial"
            className="btn-primary text-sm py-2.5 px-4 whitespace-nowrap"
            data-testid="nav-cta-primary"
          >
            Speak to an expert <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-ink"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-line bg-white"
          data-testid="mobile-nav"
        >
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2.5 text-sm font-medium border-b border-line-subtle ${
                    isActive ? "text-brand" : "text-ink"
                  }`
                }
                data-testid={`mobile-nav-${n.to.replace(/\//g, "")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-4">
              <Link
                to="/request-a-trial"
                onClick={() => setOpen(false)}
                className="btn-primary flex-1"
                data-testid="mobile-cta-primary"
              >
                Speak to an expert
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
